import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { formatMinor } from "@convex/payments/domain";
import { Warning } from "@phosphor-icons/react";

/**
 * „Banca falsă" (PRD §23): ecranul pe care ajunge consumatorul când
 * providerul e cel de test. Fiecare buton trimite un eveniment prin aceeași
 * cale ca un webhook real. Nu există în producție: query-ul întoarce null.
 */

export const Route = createFileRoute("/test/fake-bank/$attemptId")({
  head: () => ({ meta: [{ title: "Bancă de test — suuper" }, { name: "robots", content: "noindex, nofollow" }] }),
  validateSearch: (search: Record<string, unknown>): { return?: string } =>
    typeof search.return === "string" ? { return: search.return } : {},
  component: FakeBankPage,
});

type Outcome = "approve" | "decline" | "pending" | "cancel" | "duplicate_success";

const BUTTONS: Array<{ outcome: Outcome; label: string; testId: string; primary?: boolean }> = [
  { outcome: "approve", label: "Confirmă plata", testId: "fake-bank-approve", primary: true },
  { outcome: "decline", label: "Refuză", testId: "fake-bank-decline" },
  { outcome: "pending", label: "Ține în așteptare", testId: "fake-bank-pending" },
  { outcome: "duplicate_success", label: "Trimite succes duplicat", testId: "fake-bank-duplicate" },
  { outcome: "cancel", label: "Anulează", testId: "fake-bank-cancel" },
];

/** Doar înapoi pe pagina de plată a aceluiași site; niciodată către un domeniu străin. */
function safeReturnUrl(candidate: string | undefined, intentId: string): string {
  const fallback = `/pay/${intentId}?return=1`;
  if (!candidate) return fallback;
  try {
    const url = new URL(candidate, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith("/pay/")) return fallback;
    return url.pathname + url.search;
  } catch {
    return fallback;
  }
}

function FakeBankPage() {
  const { attemptId } = Route.useParams();
  const { return: returnParam } = Route.useSearch();
  const attempt = useQuery(api.payments.fake.getFakeBankAttempt, { attemptId });
  const simulate = useMutation(api.payments.fake.simulateFakeBankOutcome);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const act = async (outcome: Outcome) => {
    if (!attempt || busy) return;
    setBusy(true);
    try {
      const r = await simulate({ attemptId: attempt.attemptId as Id<"paymentAttempts">, outcome });
      setResult(r.outcome);
      if (outcome === "approve" || outcome === "decline") {
        setTimeout(() => window.location.assign(safeReturnUrl(returnParam, attempt.intentId)), 1500);
      }
    } catch (err) {
      setResult(`eroare: ${(err as Error).message}`);
    }
    setBusy(false);
  };

  return (
    <main className="min-h-screen bg-gray-900 text-white px-4 py-8">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 bg-yellow-400 text-gray-900 rounded-xl px-4 py-3 font-bold mb-6">
          <Warning size={22} weight="fill" /> BANCĂ DE TEST — nu se mișcă bani
        </div>

        {attempt === undefined && <p>Se încarcă…</p>}
        {attempt === null && <p className="text-lg font-semibold">Banca de test nu e disponibilă.</p>}

        {attempt && (
          <>
            <p className="text-sm text-gray-300">Plată către</p>
            <p className="text-2xl font-bold" data-testid="merchant-name">
              {attempt.merchantName}
            </p>
            <p className="text-4xl font-bold mt-3" data-testid="payment-amount">
              {formatMinor(attempt.amountMinor)}
            </p>
            {attempt.reference && <p className="text-gray-300 mt-1">{attempt.reference}</p>}
            <p className="text-xs text-gray-400 mt-2">
              Stare curentă: <span data-testid="payment-status">{attempt.status}</span>
            </p>

            <div className="mt-6 space-y-3">
              {BUTTONS.map((b) => (
                <button
                  key={b.outcome}
                  type="button"
                  data-testid={b.testId}
                  disabled={busy}
                  onClick={() => act(b.outcome)}
                  className={`w-full py-3 rounded-xl font-semibold disabled:opacity-50 ${
                    b.primary ? "bg-green-500 text-gray-900" : "bg-gray-700 text-white"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>

            {result && (
              <p className="mt-4 text-sm text-gray-300" data-testid="fake-bank-result" aria-live="polite">
                Rezultat: <span className="font-mono">{result}</span>
              </p>
            )}

            <a
              href={safeReturnUrl(returnParam, attempt.intentId)}
              className="mt-6 block text-center underline text-gray-300"
              data-testid="fake-bank-return"
            >
              Înapoi la suuper
            </a>
          </>
        )}
      </div>
    </main>
  );
}
